/**
 * 导航 / 面包屑工具函数
 *
 * 提供面包屑生成、菜单数据构建等通用导航功能。
 */

import type React from 'react';

/**
 * 面包屑项
 */
export interface BreadcrumbItem {
  /** 显示文本 */
  title: string;
  /** 链接路径 */
  path?: string;
  /** 图标 */
  icon?: React.ReactNode;
}

/**
 * 路由配置项（简化版）
 */
export interface RouteItem {
  /** 路径 */
  path: string;
  /** 名称 */
  name?: string;
  /** 图标 */
  icon?: React.ReactNode;
  /** 子路由 */
  routes?: RouteItem[];
  /** 是否隐藏 */
  hideInMenu?: boolean;
  /** 是否隐藏面包屑 */
  hideInBreadcrumb?: boolean;
  /** 额外数据 */
  [key: string]: unknown;
}

/**
 * 菜单项
 */
export interface MenuItem {
  /** 唯一标识 */
  key: string;
  /** 显示文本 */
  label: string;
  /** 链接路径 */
  path?: string;
  /** 图标 */
  icon?: React.ReactNode;
  /** 子菜单 */
  children?: MenuItem[];
  /** 是否禁用 */
  disabled?: boolean;
}

function joinRoutePath(parentPath: string, routePath: string): string {
  return routePath.startsWith('/')
    ? routePath
    : `${parentPath}/${routePath}`.replace(/\/+/g, '/');
}

function isDynamicIdentifierSegment(segment: string): boolean {
  return (
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    )
  );
}

/**
 * 将路径段转换为可读标题
 *
 * @param segment - 路径段
 * @returns 可读标题
 *
 * @example
 * ```ts
 * pathSegmentToTitle('user-management') // '用户管理' (需要路由配置) 或 'User Management'
 * pathSegmentToTitle('userList')        // 'User List'
 * ```
 */
export function pathSegmentToTitle(segment: string): string {
  if (!segment) return '';
  // 处理 kebab-case 和 camelCase
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * 根据当前路径和路由配置生成面包屑
 *
 * @param pathname - 当前路径
 * @param routes - 路由配置
 * @param homeTitle - 首页标题，默认 'Home'
 * @returns 面包屑项数组
 *
 * @example
 * ```ts
 * const breadcrumbs = generateBreadcrumbItems('/system/user/edit/1', routes);
 * // => [{ title: '首页', path: '/' }, { title: '系统管理', path: '/system' }, { title: '用户管理', path: '/system/user' }, { title: '编辑' }]
 * ```
 */
export function generateBreadcrumbItems(
  pathname: string,
  routes: RouteItem[],
  homeTitle: string = 'Home',
): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ title: homeTitle, path: '/' }];

  // 构建路径到路由名称的映射
  const routeMap = new Map<string, RouteItem>();
  function buildMap(routeList: RouteItem[], parentPath: string = '') {
    for (const route of routeList) {
      const fullPath = joinRoutePath(parentPath, route.path);
      routeMap.set(fullPath, route);
      if (route.routes) {
        buildMap(route.routes, fullPath);
      }
    }
  }
  buildMap(routes);

  // 按路径段逐级生成面包屑
  const segments = pathname.split('/').filter(Boolean);
  let currentPath = '';

  for (const segment of segments) {
    currentPath += `/${segment}`;
    const route = routeMap.get(currentPath);

    if (route && !route.hideInBreadcrumb) {
      items.push({
        title: route.name ?? pathSegmentToTitle(segment),
        path: currentPath,
        icon: route.icon,
      });
    } else if (!route) {
      // 可能是动态参数（如 /edit/123），跳过或显示为标题
      // 检测是否为 ID（纯数字或标准 UUID）
      if (isDynamicIdentifierSegment(segment)) {
        continue; // 跳过 ID 段
      }
      items.push({
        title: pathSegmentToTitle(segment),
        path: currentPath,
      });
    }
  }

  // 最后一项不需要链接
  if (items.length > 1) {
    const last = items[items.length - 1];
    items[items.length - 1] = { ...last, path: undefined };
  }

  return items;
}

/**
 * 根据路由配置和权限生成菜单数据
 *
 * @param routes - 路由配置
 * @param permissions - 用户权限列表（可选，不传则不做权限过滤）
 * @param options - 配置选项
 * @returns 菜单数据
 *
 * @example
 * ```ts
 * const menuData = generateMenuData(routes, userPermissions);
 * ```
 */
export function generateMenuData(
  routes: RouteItem[],
  permissions?: string[],
  options?: {
    /** 权限字段名，默认 'access' */
    accessKey?: string;
    /** 父路径前缀 */
    parentPath?: string;
  },
): MenuItem[] {
  const { accessKey = 'access', parentPath = '' } = options ?? {};
  const permissionSet = permissions ? new Set(permissions) : null;
  const result: MenuItem[] = [];

  for (const route of routes) {
    // 隐藏菜单项跳过
    if (route.hideInMenu) continue;

    // 没有名称跳过
    if (!route.name) continue;

    // 权限检查
    const access = route[accessKey] as string | undefined;
    if (access && permissionSet && !permissionSet.has(access)) continue;

    const fullPath = joinRoutePath(parentPath, route.path);

    const menuItem: MenuItem = {
      key: fullPath,
      label: route.name,
      path: fullPath,
      icon: route.icon,
    };

    // 递归处理子路由
    if (route.routes && route.routes.length > 0) {
      const children = generateMenuData(route.routes, permissions, {
        accessKey,
        parentPath: fullPath,
      });
      if (children.length > 0) {
        menuItem.children = children;
      }
    }

    result.push(menuItem);
  }

  return result;
}

/**
 * 在菜单数据中查找匹配路径的菜单项
 *
 * @param menuItems - 菜单数据
 * @param path - 要查找的路径
 * @returns 匹配的菜单项，未找到返回 null
 */
export function findMenuItemByPath(
  menuItems: MenuItem[],
  path: string,
): MenuItem | null {
  for (const item of menuItems) {
    if (item.path === path) return item;
    if (item.children) {
      const found = findMenuItemByPath(item.children, path);
      if (found) return found;
    }
  }
  return null;
}
